import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import i18n from 'i18next';

import { createIconComponent } from 'uikit/icons';
import { Checkbox } from 'uikit/inputs';
import { Loading } from 'uikit/loaders';
import { Pagination } from 'uikit/pages';
import { Typo } from 'uikit/content';

import usePagination from '../hooks/usePagination';

import NestedListContainer from '../containers/nestedListContainer';

import Count from './count';
import ListEmpty from './listEmpty';

import styles from '../styles/listBody.module.scss';

const ListBody = ({
  className = null,
  countText = '',
  data = [],
  emptyComponent = null,
  hasCheckbox = false,
  hasCount = false,
  hasPagination = false,
  hasQuery = false,
  isInitiallyExpanded = false,
  isLoading = false,
  isPaginationHiddenIfSinglePage = false,
  isUngrouped = false,
  ListElement = Fragment,
  ListItem = () => null,
  onCheckboxChange = () => {},
  selection = [],
  totalPages = 1,
}) => {
  const { currentPage, onPageClick } = usePagination();
  const hasData = Boolean(data.length);

  const renderEmptyComponent = () =>
    !hasQuery ? (
      emptyComponent
    ) : (
      <ListEmpty body={i18n.t('modify-your-search')} title={i18n.t('no-results-found')} />
    );

  const renderItems = (items = data) => (
    <ListElement>
      {items.map((item) => (
        <ListItem item={item} key={item.id} />
      ))}
    </ListElement>
  );

  const renderList = () => {
    if (isUngrouped) return renderItems();

    const initialExpandedIds = isInitiallyExpanded ? data.map(({ id }) => id) : [];

    return data.map((group) => {
      const { icon, id: groupId, items = [], name: groupName } = group;
      const count = items.length;

      return (
        <NestedListContainer
          bodyAriaLabel={groupName}
          bodyClassName={styles.body}
          hasToggle={Boolean(count)}
          header={
            <div className={styles.group}>
              {icon &&
                createIconComponent({
                  className: classnames(styles.icon, icon === 'tag' && styles.isTag),
                  icon,
                  size: 18,
                })}
              <Typo className={styles.name} weight="medium">
                {groupName}
              </Typo>
              {hasCount && <Count tooltip={`${count} ${countText}`} value={count} />}
            </div>
          }
          headerClassName={styles.header}
          headerPrepend={
            hasCheckbox && (
              <Checkbox
                className={styles.checkbox}
                id={`list-body-checkbox-${groupId}`}
                onChange={(isChecked) => onCheckboxChange(isChecked, group)}
                value={selection.some(({ id }) => id === groupId)}
              />
            )
          }
          id={groupId}
          initialExpandedIds={initialExpandedIds}
          key={groupId ?? groupName}
          toggleButtonSize="xsmall"
          toggleClassName={styles.toggle}
        >
          {renderItems(items)}
        </NestedListContainer>
      );
    });
  };

  return (
    <div className={classnames(styles.element, className)}>
      <div className={classnames(styles.list, isLoading && styles.isHidden)}>
        {hasData ? renderList() : renderEmptyComponent()}
      </div>
      {isLoading && (
        <div className={styles.loaderContainer}>
          <Loading className={styles.loader} />
        </div>
      )}
      {hasData && hasPagination && (
        <Pagination
          currentPage={currentPage}
          isHiddenIfSinglePage={isPaginationHiddenIfSinglePage}
          onPageClick={onPageClick}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

ListBody.propTypes = {
  className: PropTypes.string,
  countText: PropTypes.string,
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        id: PropTypes.number,
        items: PropTypes.arrayOf(PropTypes.shape({})),
        name: PropTypes.string,
      }),
    ),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
  emptyComponent: PropTypes.node,
  hasCheckbox: PropTypes.bool,
  hasCount: PropTypes.bool,
  hasPagination: PropTypes.bool,
  hasQuery: PropTypes.bool,
  isInitiallyExpanded: PropTypes.bool,
  isLoading: PropTypes.bool,
  isPaginationHiddenIfSinglePage: PropTypes.bool,
  isUngrouped: PropTypes.bool,
  ListElement: PropTypes.elementType,
  ListItem: PropTypes.elementType,
  onCheckboxChange: PropTypes.func,
  selection: PropTypes.arrayOf(PropTypes.shape({})),
  totalPages: PropTypes.number,
};

export default React.memo(ListBody);
